import { VIEWER_TOOL_REGISTRY, getToolRegistryEntry, toolPlacementAllowed } from './toolRegistry';

export const TOOL_LAYOUT_PREF_NAMESPACE = 'viewer.tool_layout';
export const TOOL_LAYOUT_STORAGE_KEY = 'kairos.viewerToolLayout';

const PLACEMENTS = ['top', 'dock', 'context'];
const TOOLBAR_SIZES = ['compact', 'comfortable', 'large'];
const TOOLBAR_FONT_SIZES = ['follow', 'small', 'standard', 'large'];
const DENSITIES = ['compact', 'comfortable', 'large'];
const DEDUPE_POLICIES = ['clinical-defaults', 'allow-context-duplicates'];
const IMAGE_TOOL_CATEGORIES = new Set(['windowing', 'measure', 'ROI', 'annotation']);
const CONFIGURABLE_PLACEMENT_TOOL_IDS = Object.freeze({
  top: ['scroll', 'wl', 'measure', 'angle', 'cobb', 'roi', 'rect', 'ellipse', 'arrow', 'text', 'pen', 'level'],
  dock: ['measure', 'angle', 'cobb', 'roi', 'arrow', 'text', 'pen', 'rect', 'ellipse', 'level', 'freezeViewport'],
  context: ['wl', 'measure', 'angle', 'cobb', 'roi', 'arrow', 'text', 'pen', 'rect', 'ellipse', 'level'],
});

const CONFIGURABLE_TOP_GROUP_IDS = Object.freeze({
  scroll: 'quick-navigation', wl: 'quick-navigation',
  measure: 'quick-measure', angle: 'quick-measure', cobb: 'quick-measure',
  roi: 'quick-roi', rect: 'quick-roi', ellipse: 'quick-roi',
  arrow: 'quick-annotate', text: 'quick-annotate', pen: 'quick-annotate', level: 'quick-annotate',
});

const DEFAULT_GROUPS = Object.freeze({
  'layout-flow': { label: 'LAYOUT / FLOW', placement: 'top', collapsedByDefault: false, tools: ['layout-1x1', 'layout-2x2', 'layout-3x3', 'linked-scroll', 'reference-lines'] },
  'quick-navigation': { label: 'NAVIGATION', placement: 'top', collapsedByDefault: false, tools: ['scroll', 'wl'] },
  'quick-measure': { label: 'MEASUREMENTS', placement: 'top', collapsedByDefault: false, tools: ['measure'] },
  'quick-roi': { label: 'ROI', placement: 'top', collapsedByDefault: false, tools: ['roi'] },
  'quick-annotate': { label: 'ANNOTATIONS', placement: 'top', collapsedByDefault: false, tools: ['arrow', 'text'] },
  advanced: { label: 'ADVANCED', placement: 'top', collapsedByDefault: false, tools: ['mpr', 'hanging-protocol-editor', 'report'] },
  workstation: { label: 'WORKSTATION', placement: 'top', collapsedByDefault: false, tools: ['shortcut-editor'] },
  roi: { label: 'ROI', placement: 'dock', collapsedByDefault: false, tools: ['roi'] },
  measure: { label: 'MEASURE', placement: 'dock', collapsedByDefault: false, tools: ['measure', 'angle', 'cobb'] },
  annotate: { label: 'ANNOTATE', placement: 'dock', collapsedByDefault: false, tools: ['arrow', 'text'] },
  draw: { label: 'DRAW', placement: 'dock', collapsedByDefault: false, tools: ['pen', 'rect', 'ellipse', 'level'] },
  status: { label: 'STATUS', placement: 'dock', collapsedByDefault: false, tools: ['freezeViewport'] },
  windowing: { label: 'WINDOW / LEVEL', placement: 'context', collapsedByDefault: false, tools: ['wl'] },
  'viewport-tools': { label: 'VIEWPORT TOOLS', placement: 'context', collapsedByDefault: false, tools: ['measure', 'angle', 'cobb', 'roi', 'arrow', 'text', 'pen', 'rect', 'ellipse', 'level'] },
});

const DEFAULT_LAYOUT = {
  version: 1,
  toolbarSize: 'compact',
  toolbarFontSize: 'follow',
  topGroups: ['layout-flow', 'quick-navigation', 'quick-measure', 'quick-roi', 'quick-annotate', 'advanced', 'workstation'],
  dockGroups: ['roi', 'measure', 'annotate', 'draw', 'status'],
  contextGroups: ['windowing', 'viewport-tools'],
  groups: DEFAULT_GROUPS,
  disabledTools: [],
  dedupePolicy: 'allow-context-duplicates',
  dock: { position: 'left', docked: true, widthPx: 72, iconSizePx: 22 },
  top: { heightPx: 46, density: 'compact', wrapMode: 'wrap' },
  context: { enabled: true, density: 'compact' },
};

export const DEFAULT_TOOL_LAYOUT = Object.freeze(DEFAULT_LAYOUT);

const asObject = value => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
const uniq = (values) => Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean).map(String)));
const clampNumber = (value, min, max, fallback) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return fallback;
  return Math.max(min, Math.min(max, Math.round(next)));
};
const pickEnum = (value, allowed, fallback) => allowed.includes(value) ? value : fallback;

export const TOOLBAR_FONT_SCALES = Object.freeze({
  small: 0.9,
  standard: 1,
  comfortable: 1.1,
  large: 1.25,
});

export const getToolbarFontScale = (value = {}) => {
  const source = asObject(value);
  const preset = pickEnum(source.toolbarFontSize, TOOLBAR_FONT_SIZES, DEFAULT_LAYOUT.toolbarFontSize);
  let scale;
  if (preset === 'small') scale = TOOLBAR_FONT_SCALES.small;
  else if (preset === 'standard') scale = TOOLBAR_FONT_SCALES.standard;
  else if (preset === 'large') scale = TOOLBAR_FONT_SCALES.large;
  else {
    const toolbarSize = pickEnum(source.toolbarSize, TOOLBAR_SIZES, DEFAULT_LAYOUT.toolbarSize);
    scale = toolbarSize === 'large'
      ? TOOLBAR_FONT_SCALES.large
      : toolbarSize === 'comfortable'
        ? TOOLBAR_FONT_SCALES.comfortable
        : TOOLBAR_FONT_SCALES.standard;
  }
  return Math.max(TOOLBAR_FONT_SCALES.small, Math.min(TOOLBAR_FONT_SCALES.large, scale));
};

const normalizeGroup = (id, raw, disabledTools) => {
  const source = asObject(raw);
  const placement = pickEnum(source.placement, PLACEMENTS, null);
  if (!placement) return null;
  const tools = uniq(source.tools).filter(toolId => !disabledTools.has(toolId) && toolPlacementAllowed(toolId, placement));
  return {
    label: String(source.label || id).slice(0, 48),
    placement,
    collapsedByDefault: Boolean(source.collapsedByDefault),
    tools,
  };
};

const normalizeGroupOrder = (requested, groups, placement, fallback) => {
  const ordered = uniq(requested).filter(groupId => groups[groupId]?.placement === placement);
  return ordered.length ? ordered : fallback.filter(groupId => groups[groupId]?.placement === placement);
};

const defaultForSize = (toolbarSize) => {
  if (toolbarSize === 'large') return { heightPx: 64, dockWidthPx: 96, iconSizePx: 30, density: 'large' };
  if (toolbarSize === 'comfortable') return { heightPx: 54, dockWidthPx: 84, iconSizePx: 26, density: 'comfortable' };
  return { heightPx: 46, dockWidthPx: 72, iconSizePx: 22, density: 'compact' };
};

export const normalizeToolLayoutPreferences = (value = {}) => {
  const source = asObject(value);
  const toolbarSize = pickEnum(source.toolbarSize, TOOLBAR_SIZES, DEFAULT_LAYOUT.toolbarSize);
  const toolbarFontSize = pickEnum(source.toolbarFontSize, TOOLBAR_FONT_SIZES, DEFAULT_LAYOUT.toolbarFontSize);
  const sizing = defaultForSize(toolbarSize);
  const disabledTools = new Set(uniq(source.disabledTools).filter(toolId => getToolRegistryEntry(toolId)));
  const rawGroups = { ...DEFAULT_GROUPS, ...asObject(source.groups) };
  const groups = Object.fromEntries(
    Object.entries(rawGroups)
      .map(([id, group]) => [id, normalizeGroup(id, group, disabledTools)])
      .filter(([, group]) => group)
  );
  const topGroups = normalizeGroupOrder(source.topGroups, groups, 'top', DEFAULT_LAYOUT.topGroups);
  const dockGroups = normalizeGroupOrder(source.dockGroups, groups, 'dock', DEFAULT_LAYOUT.dockGroups);
  const contextGroups = Array.isArray(source.contextGroups)
    ? normalizeGroupOrder(source.contextGroups, groups, 'context', [])
    : normalizeGroupOrder(source.contextGroups, groups, 'context', DEFAULT_LAYOUT.contextGroups);
  const dockSource = asObject(source.dock);
  const topSource = asObject(source.top);
  const contextSource = asObject(source.context);

  return {
    version: 1,
    toolbarSize,
    toolbarFontSize,
    topGroups,
    dockGroups,
    contextGroups,
    groups,
    disabledTools: Array.from(disabledTools),
    dedupePolicy: pickEnum(source.dedupePolicy, DEDUPE_POLICIES, DEFAULT_LAYOUT.dedupePolicy),
    dock: {
      position: dockSource.position === 'right' ? 'right' : 'left',
      docked: dockSource.docked !== false,
      widthPx: clampNumber(dockSource.widthPx, 56, 96, sizing.dockWidthPx),
      iconSizePx: clampNumber(dockSource.iconSizePx, 18, 32, sizing.iconSizePx),
    },
    top: {
      heightPx: clampNumber(topSource.heightPx, 34, 72, sizing.heightPx),
      density: pickEnum(topSource.density, DENSITIES, sizing.density),
      wrapMode: topSource.wrapMode === 'scroll' ? 'scroll' : 'wrap',
    },
    context: {
      enabled: contextSource.enabled !== false,
      density: pickEnum(contextSource.density, DENSITIES, DEFAULT_LAYOUT.context.density),
    },
  };
};

const placementGroupOrderKey = placement => placement === 'top' ? 'topGroups' : placement === 'dock' ? 'dockGroups' : 'contextGroups';
const canonicalGroupIdForTool = (placement, toolId) => (placement === 'top' ? CONFIGURABLE_TOP_GROUP_IDS[toolId] : null) || Object.entries(DEFAULT_GROUPS)
  .find(([, group]) => group.placement === placement && group.tools.includes(toolId))?.[0] || null;

export const getConfigurableToolsForPlacement = placement => (CONFIGURABLE_PLACEMENT_TOOL_IDS[placement] || [])
  .map(toolId => getToolRegistryEntry(toolId))
  .filter(Boolean)
  .map(tool => placement === 'context' && tool.id === 'wl'
    ? { ...tool, label: 'Window / Level presets', ariaLabel: 'Show window and level presets' }
    : tool);

export const isToolEnabledForPlacement = (layoutValue, placement, toolId) => {
  const layout = normalizeToolLayoutPreferences(layoutValue || DEFAULT_TOOL_LAYOUT);
  if (placement === 'context' && layout.context?.enabled === false) return false;
  const orderKey = placementGroupOrderKey(placement);
  return (layout[orderKey] || []).some(groupId => (layout.groups[groupId]?.tools || []).includes(toolId));
};

export const setToolEnabledForPlacement = (layoutValue, placement, toolId, enabled) => {
  if (!CONFIGURABLE_PLACEMENT_TOOL_IDS[placement]?.includes(toolId)) {
    return normalizeToolLayoutPreferences(layoutValue || DEFAULT_TOOL_LAYOUT);
  }
  const base = normalizeToolLayoutPreferences(layoutValue || DEFAULT_TOOL_LAYOUT);
  const groups = Object.fromEntries(Object.entries(base.groups).map(([groupId, group]) => [
    groupId,
    { ...group, tools: [...(group.tools || [])] },
  ]));
  Object.values(groups).forEach(group => {
    if (group.placement === placement) group.tools = group.tools.filter(id => id !== toolId);
  });

  const orderKey = placementGroupOrderKey(placement);
  let groupOrder = [...(base[orderKey] || [])];
  if (enabled) {
    const canonicalGroupId = canonicalGroupIdForTool(placement, toolId);
    if (!canonicalGroupId) return base;
    const template = DEFAULT_GROUPS[canonicalGroupId];
    groups[canonicalGroupId] = {
      ...template,
      ...(groups[canonicalGroupId] || {}),
      placement,
      tools: uniq([...(groups[canonicalGroupId]?.tools || []), toolId]),
    };
    if (!groupOrder.includes(canonicalGroupId)) groupOrder.push(canonicalGroupId);
  }

  return normalizeToolLayoutPreferences({
    ...base,
    groups,
    [orderKey]: groupOrder,
    disabledTools: enabled ? base.disabledTools.filter(id => id !== toolId) : base.disabledTools,
    ...(placement === 'context' && enabled ? { context: { ...base.context, enabled: true } } : {}),
  });
};

export const setToolPlacementEnabled = (layoutValue, placement, enabled) => {
  const base = normalizeToolLayoutPreferences(layoutValue || DEFAULT_TOOL_LAYOUT);
  if (placement !== 'context') return base;
  const contextGroups = enabled && base.contextGroups.length === 0
    ? [...DEFAULT_LAYOUT.contextGroups]
    : [...base.contextGroups];
  return normalizeToolLayoutPreferences({
    ...base,
    contextGroups,
    context: { ...base.context, enabled: Boolean(enabled) },
  });
};

export const selectToolsForPlacement = (layoutValue, placement) => {
  const layout = normalizeToolLayoutPreferences(layoutValue);
  if (placement === 'context' && layout.context?.enabled === false) return [];
  const groupsForPlacement = placement === 'top' ? layout.topGroups : placement === 'dock' ? layout.dockGroups : layout.contextGroups;
  const allSelected = new Map();
  ['top', 'dock', 'context'].forEach((candidatePlacement) => {
    const groupIds = candidatePlacement === 'top' ? layout.topGroups : candidatePlacement === 'dock' ? layout.dockGroups : layout.contextGroups;
    groupIds.forEach(groupId => (layout.groups[groupId]?.tools || []).forEach(toolId => allSelected.set(toolId, (allSelected.get(toolId) || new Set()).add(candidatePlacement))));
  });
  const keepToolInPlacement = (toolId) => {
    const tool = getToolRegistryEntry(toolId);
    const placements = allSelected.get(toolId) || new Set();
    if (layout.dedupePolicy === 'allow-context-duplicates' && placement === 'context') return true;
    if (placement === 'context' && IMAGE_TOOL_CATEGORIES.has(tool?.category)) return true;
    if (placements.size <= 1) return true;
    if (placement === 'top') return true;
    if (placement === 'dock' && IMAGE_TOOL_CATEGORIES.has(tool?.category)) return true;
    if (placement === 'context') return !placements.has('dock') && !placements.has('top');
    return false;
  };
  const ids = [];
  groupsForPlacement.forEach(groupId => {
    (layout.groups[groupId]?.tools || []).forEach(toolId => {
      if (!ids.includes(toolId) && keepToolInPlacement(toolId)) ids.push(toolId);
    });
  });
  return ids.map(toolId => VIEWER_TOOL_REGISTRY.find(tool => tool.id === toolId)).filter(Boolean);
};

export const loadToolLayoutPreferences = () => {
  if (typeof window === 'undefined') return DEFAULT_TOOL_LAYOUT;
  try {
    return normalizeToolLayoutPreferences(JSON.parse(window.localStorage.getItem(TOOL_LAYOUT_STORAGE_KEY) || '{}'));
  } catch (_) {
    return DEFAULT_TOOL_LAYOUT;
  }
};

export const saveToolLayoutPreferences = (value) => {
  const normalized = normalizeToolLayoutPreferences(value);
  if (typeof window !== 'undefined') {
    try { window.localStorage.setItem(TOOL_LAYOUT_STORAGE_KEY, JSON.stringify(normalized)); } catch (_) {}
  }
  return normalized;
};
